import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, SearchIcon, ShieldCheckIcon, MessageSquareIcon, ArrowRightIcon } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from './AuthContext';
const FEATURES = [{
  icon: SearchIcon,
  title: 'Find anything, instantly',
  desc: 'Describe what you need and let your AI agent search every listing for you.'
}, {
  icon: MessageSquareIcon,
  title: 'Chat with sellers safely',
  desc: 'Negotiate and ask questions while Talab watches for scams in real time.'
}, {
  icon: ShieldCheckIcon,
  title: 'Verified, trusted marketplace',
  desc: 'Buy and sell with verified businesses and identity-checked individuals.'
}];
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1] as const
    }
  }
};
export function WelcomeScreen() {
  const {
    openLogin,
    enterAsGuest
  } = useAuth();
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-[150] bg-white flex flex-col overflow-hidden" data-id="element-2148">
      {/* Ambient accent — subtle, premium */}
      <div className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full bg-primary-indigo/5 blur-3xl" data-id="element-2149" />
      <div className="pointer-events-none absolute top-1/3 -left-24 w-64 h-64 rounded-full bg-ai-purple/5 blur-3xl" data-id="element-2150" />

      {/* Scrollable content */}
      <div className="relative flex-1 min-h-0 overflow-y-auto px-7 pt-16 pb-4" data-id="element-2151">
        <motion.div variants={container} initial="hidden" animate="show" className="flex flex-col" data-id="element-2152">
          {/* Brand */}
          <motion.div variants={item} className="mb-9" data-id="element-2153">
            <Logo size="md" data-id="element-2154" />
          </motion.div>

          {/* Hero copy */}
          <motion.h1 variants={item} className="font-display font-bold text-[28px] leading-[1.15] text-dark-navy tracking-tight mb-3" data-id="element-2155">
            The marketplace that{' '}
            <span className="text-primary-indigo" data-id="element-2156">finds it for you.</span>
          </motion.h1>
          <motion.p variants={item} className="text-[15px] leading-relaxed text-slate-500 mb-9 max-w-[300px]" data-id="element-2157">
            Talab is your AI shopping agent — search, chat and buy across
            thousands of listings, all in one place.
          </motion.p>

          {/* Feature list */}
          <div className="space-y-5" data-id="element-2158">
            {FEATURES.map(f => <motion.div key={f.title} variants={item} className="flex items-start gap-3.5" data-id="element-2159">
                <span className="flex-shrink-0 w-11 h-11 rounded-2xl bg-primary-indigo/8 flex items-center justify-center" data-id="element-2160">
                  <f.icon className="w-[20px] h-[20px] text-primary-indigo" data-id="element-2161" />
                </span>
                <div className="min-w-0 pt-0.5" data-id="element-2162">
                  <p className="text-[14.5px] font-bold text-dark-navy leading-tight mb-0.5" data-id="element-2163">
                    {f.title}
                  </p>
                  <p className="text-[12.5px] text-slate-500 leading-snug" data-id="element-2164">
                    {f.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>

      {/* Bottom action area */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.45,
      duration: 0.4
    }} className="relative flex-shrink-0 px-7 pt-4 pb-9 bg-white border-t border-slate-50" data-id="element-2165">
        <button onClick={openLogin} className="w-full flex items-center justify-center gap-2 py-4 bg-primary-indigo text-white text-[15px] font-bold rounded-2xl shadow-[0_8px_24px_rgba(79,70,229,0.25)] active:scale-[0.98] transition-transform" data-id="element-2166">
          <SparklesIcon className="w-[18px] h-[18px]" data-id="element-2167" />
          Sign in to get started
        </button>
        <button onClick={enterAsGuest} className="w-full flex items-center justify-center gap-1.5 py-4 mt-2 text-slate-500 text-[14px] font-semibold rounded-2xl active:bg-slate-50 transition-colors" data-id="element-2168">
          Continue without login
          <ArrowRightIcon className="w-4 h-4" data-id="element-2169" />
        </button>
        <p className="text-center text-[11px] text-slate-400 leading-relaxed mt-3 px-2" data-id="element-2170">
          By continuing you agree to Talab&apos;s Terms of Service and Privacy
          Policy.
        </p>
      </motion.div>
    </motion.div>;
}